import React, { useEffect, useState, useRef } from "react";
import { toAbsoluteUrl } from "../../helpers";
import { useTheme } from "../../../app/context/ThemeContext";
import useGetChats from "../../../app/hooks/useGetChats";
import { createChat, deleteChatRoom } from "../../../app/services/chats";
import { useAuth } from "../../../app/modules/auth";
import { longWordSlicingWithCustomLength } from "../../../app/helper-functions/CheckString";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentChat, setMessages, addMessage, clearMessages, SUGGESTED_PROMPTS } from "../../../app/store/slice/ai-slice";
interface ChatSession {
  id: string;
  title: string;
  messages: any[] | null;
  timestamp: Date;
}

const AIAssistant: React.FC = () => {
  const dispatch = useDispatch();
  const { currentChat, messages } = useSelector((state: any) => state.aiReducer || { currentChat: null, messages: [] });

  const [isOpen, setIsOpen] = useState(false);
  const { currentUser } = useAuth()
  const { id } = currentUser?.data.user

  const [activeTab, setActiveTab] = useState<"new" | "history">("new");
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [input, setInput] = useState("");
  const [showIntro, setShowIntro] = useState(true); // 👋 intro popup visibility
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const promptsScrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { getChatsHandler, chatRooms, setChatRooms, getChatHistoryHandler, sendMessageHandler } = useGetChats();
  const { theme } = useTheme()
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowIntro(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    getChatsHandler()
  }, [activeTab]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Scroll to bottom when opening the assistant
  useEffect(() => {
    if (isOpen && messages && messages.length > 0) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        // focus input when opening the assistant
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Scroll to bottom when switching to "new" tab
  useEffect(() => {
    if (activeTab === "new" && messages && messages.length > 0) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [activeTab]);

  const toggleChat = () => setIsOpen(!isOpen);

  // Get current chat display name with truncation
  const getCurrentChatName = () => {
    if (!currentChat) return "New Chat";

    const chatName = currentChat?.title || currentChat?.name || "New Chat";

    return longWordSlicingWithCustomLength(chatName, 10);
  };

  const saveCurrentChatToHistory = () => {
    if (messages?.length === 0) return;

    const firstUserMessage = messages?.find((msg: any) => msg.from === "user")?.text || "New Chat";
    const chatTitle = firstUserMessage.length > 50
      ? firstUserMessage.substring(0, 50) + "..."
      : firstUserMessage;

    const newSession: ChatSession = {
      id: currentSession?.id || Date.now().toString(),
      title: chatTitle,
      messages: [...messages],
      timestamp: new Date(),
    };

    setChatRooms((prev: any) => {
      const existingIndex = prev.findIndex((s: any) => s.id === newSession.id);
      if (existingIndex !== -1) {
        // Update existing session
        const updated = [...prev];
        updated[existingIndex] = newSession;
        return updated;
      }
      // Add new session
      return [...prev, newSession];
    });
  };
  const startNewChat = async () => {
    try {
      if (messages?.length > 0 && !currentSession) {
        saveCurrentChatToHistory();
      }
      const payload = { userId: id }
      const newChat: any = await createChat(payload)
      if (newChat.data.success) {
        setChatRooms((prev: any) => [...prev, newChat.data.data])
        dispatch(setCurrentChat(newChat.data.data));
      }
      dispatch(clearMessages());
      setCurrentSession(null);
      setActiveTab("new");
      setInput("");
      setIsTyping(false);
      // focus input for the new chat
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (err) {
      // console.error('Error creating chat:', err);
    }
  };

  const loadChatHistory = async (chat: any) => {
    try {
      // Always fetch fresh history from API using the hook
      await getChatHistoryHandler(chat?.id);
      dispatch(setCurrentChat(chat));
    } catch (err) {
      // console.error('Error loading chat history:', err);
      dispatch(clearMessages());
    }
  };

  // Handle tab switching - ensure proper state management
  const handleTabSwitch = (tab: "new" | "history") => {
    if (tab === "history" && isTyping) {
      // Don't switch while AI is responding
      return;
    }
    setActiveTab(tab);
  };

  // Scroll prompts carousel
  const scrollPrompts = (direction: 'left' | 'right') => {
    if (promptsScrollRef.current) {
      const scrollAmount = 200;
      promptsScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isTyping) return;

    let activeChatId = currentChat?.id;

    // Check if we have a current chat, if not create one
    if (!currentChat) {
      try {
        const payload = { userId: id }
        const newChat: any = await createChat(payload)
        if (newChat.data.success) {
          dispatch(setCurrentChat(newChat.data.data));
          setChatRooms((prev: any) => [...prev, newChat.data.data])
          activeChatId = newChat.data.data.id;
        } else {
          // console.error('Failed to create chat');
          return;
        }
      } catch (err) {
        // console.error('Error creating chat:', err);
        return;
      }
    }

    const userMessage = input.trim();

    // Optimistically add user message to UI
    const userMsg = { prompt: userMessage };
    dispatch(setMessages(messages ? [...messages, userMsg] : [userMsg]));
    setInput("");
    setIsTyping(true);

    try {
      // Send message to API
      const payload = {
        prompt: userMessage,
        userId: id,
        chatId: activeChatId,
      };
      const updateKeywords = ['update', 'change', 'modify', 'edit', 'set', 'archive'];
      const isUpdateRequest = updateKeywords.some((keyword) => userMessage.toLowerCase().includes(keyword));

      const response: any = await sendMessageHandler(payload,isUpdateRequest);

      if (!response?.data?.embeddingError) {
        // Add AI response to messages
        const aiMsg = { response: response.data.message };
        dispatch(addMessage(aiMsg));

        // Update chat rooms with the latest message and title
        setChatRooms((prev: any) => {
          const updatedRooms = prev.map((room: any) => {
            if (room.id === activeChatId) {
              // Set title from first message if not already set
              const isFirstMessage = !room.history || room.history.length === 0;
              const chatTitle = isFirstMessage
                ? (userMessage.length > 50 ? userMessage.substring(0, 50) + "..." : userMessage)
                : room.title;

              return {
                ...room,
                title: chatTitle,
                history: [...(room.history || []), userMsg, aiMsg],
                updatedAt: new Date().toISOString(),
              };
            }
            return room;
          });
          return updatedRooms;
        });

        // Update currentChat with the title
        if (currentChat && currentChat.id === activeChatId) {
          const isFirstMessage = !currentChat.history || currentChat.history.length === 0;
          dispatch(setCurrentChat({
            ...currentChat,
            title: isFirstMessage
              ? (userMessage.length > 50 ? userMessage.substring(0, 50) + "..." : userMessage)
              : currentChat.title,
          }));
        }
      } else {
        // Handle error response
        const errorMsg = { response:response?.data?.embeddingError || "Sorry, I couldn't process your request. Please try again." };
        dispatch(addMessage(errorMsg));
      }
    } catch (err:any) {
      // console.error('Error sending message:', err);
      const errorMsg = { response: err?.response?.data?.message || "An error occurred. Please try again." };
      dispatch(addMessage(errorMsg));
    } finally {
      setIsTyping(false);
      // focus input after response
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };


  return (
    <>
      <div className="ai-assistant-container">
        {/* Floating Button with animation */}
        <div className="ai-button-container">
          {!isOpen && (
            <>
              <div className="ai-button-container">
                {/* Spinning orange border */}
                <div className="ai-spinning-border" />
                <button
                  className="btn rounded-circle shadow-lg d-flex align-items-center justify-content-center ai-floating-button"
                  style={{
                    backgroundColor: theme === 'dark' ? "#2c3a4d" : "#eff2f5",
                  }}
                  onClick={toggleChat}
                >
                  <img
                    src={toAbsoluteUrl("/media/logos/aiAssistant.svg")}
                    alt="AI"
                    className="ai-logo"
                  />
                </button>
              </div>

              {/* Intro popup message */}
              {showIntro && (
                <div className="bg-white text-dark shadow-lg p-2 rounded ai-intro-popup">
                  👋 Hi, I&apos;m your AI assistant! <br />How can I help you?
                </div>
              )}
            </>
          )}
        </div>

        {/* Chat Window */}
        {isOpen && (
          <div className={`card shadow-lg ai-chat-window ${theme === 'dark' ? 'theme__div' : ''}`}>
            <div className="card-header px-3 bg-primary text-white d-flex justify-content-between align-items-center ai-chat-header">
              <div className="d-flex align-items-center gap-2">
                {/* <img
                  src={toAbsoluteUrl("/media/logos/aiAssistant.svg")}
                  alt="AI"
                  style={{ width: "30px", height: "30px" }}
                /> */}
                <span className="fw-bold fs-4"> B4iGO AI Assistant</span>
              </div>
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-sm "
                  onClick={startNewChat}
                  title="Start New Chat"
                >
                  <i className={`bi bi-plus-circle fs-3 ${theme === 'dark' ? 'text-white' : 'text-dark'}`}></i>
                </button>
                <button
                  className="btn btn-sm btn-light ai-close-button"
                  onClick={toggleChat}
                >
                  ✖
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div
              className={`d-flex `}
              style={{ backgroundColor: theme === 'dark' ? '#1e2732' : '#fff' }}
            >
              <button
                className={`flex-fill btn border-0 rounded-right d-flex align-items-center justify-content-center gap-2 py-3 ${activeTab === "new"
                  ? "bg-primary text-white"
                  : theme === 'dark'
                    ? "text-light"
                    : "text-dark"
                  }`}
                style={{
                  transition: "all 0.3s ease",
                  backgroundColor: activeTab === "new" ? undefined : (theme === 'dark' ? '#1e2732' : '#fff')
                }}
                onClick={() => handleTabSwitch("new")}
                title={currentChat?.title || currentChat?.name || "New Chat"}
              >
                <i className={`bi bi-chat-dots fs-4 ${theme === 'dark' ? 'text-white' : 'text-dark'}`}></i>
                <span className="fw-semibold text-truncate fs-6">{getCurrentChatName()}</span>
              </button>
              <button
                className={`flex-fill btn border-0 rounded-left d-flex align-items-center justify-content-center gap-1 px-1 py-3 ${activeTab === "history"
                  ? "bg-primary text-white"
                  : theme === 'dark'
                    ? "text-light"
                    : "text-dark"
                  }`}
                style={{
                  transition: "all 0.3s ease",
                  backgroundColor: activeTab === "history" ? undefined : (theme === 'dark' ? '#1e2732' : '#fff'),
                  opacity: isTyping ? 0.6 : 1,
                  cursor: isTyping ? 'not-allowed' : 'pointer'
                }}
                onClick={() => handleTabSwitch("history")}
                disabled={isTyping}
              >
                <i className={`bi bi-clock-history fs-4 ${theme === 'dark' ? 'text-white' : 'text-dark'}`}></i>
                <span className="fw-semibold fs-6">Previous Chats</span>
                {chatRooms?.length > 0 && (
                  <span className={`badge rounded-pill ms-1 ${theme === 'dark' ? 'bg-secondary text-dark' : 'bg-light text-dark'}`}>
                    {chatRooms?.length}
                  </span>
                )}
              </button>
            </div>

            <div className={`card-body overflow-auto ai-chat-body px-5 ${theme === 'dark' ? 'theme__div' : ''}`}>
              {activeTab === "new" ? (
                <>
                  {(!messages || messages.length === 0) && (
                    <div className="text-muted text-center ai-empty-state">
                      <div className="ai-empty-state-icon">💬</div>
                      <div className="ai-empty-state-text">
                        Start chatting with the B4i<span className="text-primary text-bold">GO</span> AI assistant!
                      </div>
                    </div>
                  )}

                  {messages?.map((msg: any, idx: any) => (
                    <div
                      key={idx}
                      className={`d-flex mb-3 ${msg.prompt
                        ? "justify-content-end"
                        : "justify-content-start"
                        }`}
                    >
                      <div
                        className={`p-3 rounded ai-message ${msg.prompt
                          ? "bg-primary text-white ai-message-user"
                          : `ai-message-assistant ${theme === 'dark' ? 'theme__div' : 'bg-light text-dark'}`
                          }`}
                      >
                        {msg?.prompt || msg?.response}
                      </div>
                    </div>
                  ))}

                  {isTyping && (
                    <div className="d-flex justify-content-start mb-3 ai-message-assistant">
                      <div className={`p-3 rounded ai-typing-indicator ${theme === 'dark' ? 'theme__div' : 'bg-light'}`}>
                        <div className="ai-typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </>
              ) : (
                <>
                  {/* Previous Chats View */}
                  {chatRooms?.length === 0 ? (
                    <div className="text-muted text-center ai-empty-state">
                      <div className="ai-empty-state-icon">📋</div>
                      <div className="ai-empty-state-text">
                        No previous chats yet. Start a conversation to see your chat history here!
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {chatRooms && chatRooms?.sort((a: any, b: any) => b.updatedAt - a.updatedAt)
                        .map((session: any) => (
                          <div
                            key={session.id}
                            className={`p-3 rounded border cursor-pointer ${theme === 'dark'
                              ? 'border-secondary bg-dark text-light hover-bg-secondary'
                              : 'border-light bg-secondary text-dark hover-bg-white'
                              }`}
                            style={{
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                            onClick={() => {
                              setCurrentSession(session);
                              setActiveTab("new");
                              loadChatHistory(session);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-2px)";
                              e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "none";
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <div className="fw-bold mb-1 d-flex align-items-center gap-2">
                                  <i className="bi bi-chat-left-text text-primary"></i>
                                  {session?.title}
                                </div>
                                <small className={theme === 'dark' ? 'text-muted' : 'text-dark'}>
                                  {/* {session.title} message{session.messages.length !== 1 ? 's' : ''} */}
                                  • {new Date(session.updatedAt).toLocaleDateString()} {new Date(session.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </small>
                              </div>
                              <button
                                className="btn btn-sm btn-danger ms-2"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const deleteSuccess = await deleteChatRoom(session.id, id);
                                  if (deleteSuccess?.data?.success) {
                                    dispatch(setCurrentChat(null));
                                    setChatRooms((prev: any) =>
                                      prev.filter((s: any) => s.id !== session.id)
                                    );
                                    if (deleteSuccess?.data?.data?.chatId === session.id) {
                                      setCurrentSession(null);
                                      dispatch(clearMessages());
                                    }
                                  }

                                }}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {activeTab === "new" && (
              <div className={`card-footer border-0 px-2 py-3 ${theme === 'dark' ? 'theme__div' : 'bg-white'}`}>
                {/* Suggested Prompts Carousel */}
                <div className="mb-3 position-relative d-flex align-items-center">
                  {/* Left Arrow */}
                  <button
                    type="button"
                    className={`btn btn-sm rounded-circle ${theme === 'dark' ? 'btn-dark' : 'btn-light border'} shadow-sm`}
                    style={{
                      position: 'absolute',
                      left: '0',
                      zIndex: 10,
                      width: '32px',
                      height: '32px',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onClick={() => scrollPrompts('left')}
                    disabled={isTyping}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>

                  {/* Scrollable Prompts */}
                  <div
                    ref={promptsScrollRef}
                    className="d-flex gap-2 px-5 pt-1 mx-5"
                    style={{
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      whiteSpace: 'nowrap',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      WebkitOverflowScrolling: 'touch',
                    }}
                  >
                    <style>{`
                        .d-flex::-webkit-scrollbar {
                          display: none;
                        }
                      `}</style>
                    {SUGGESTED_PROMPTS?.map((item: any, index: any) => (
                      <button
                        key={index}
                        type="button"
                        className={`btn btn-sm rounded-pill ${theme === "dark" ? "btn-outline-light" : "btn-outline-secondary"
                          }`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.9rem",
                          transition: "all 0.2s ease",
                          border:
                            theme === "dark"
                              ? "1px solid #4a5568"
                              : "1px solid #dee2e6",
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                          color: theme === "dark" ? "" : "#495057",
                        }}
                        onClick={() => {
                          setInput(item.prompt);
                          // focus the input after clicking a suggested prompt
                          setTimeout(() => inputRef.current?.focus(), 30);
                        }}
                        disabled={isTyping}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        {item.label}
                      </button>
                    ))}

                  </div>

                  {/* Right Arrow */}
                  <button
                    type="button"
                    className={`btn btn-sm rounded-circle ${theme === 'dark' ? 'btn-dark' : 'btn-light border'} shadow-sm`}
                    style={{
                      position: 'absolute',
                      right: '0',
                      zIndex: 10,
                      width: '32px',
                      height: '32px',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onClick={() => scrollPrompts('right')}
                    disabled={isTyping}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </div>


                <form
                  onSubmit={handleSend}
                  className="d-flex align-items-center ai-chat-footer"
                >
                  <input
                    ref={inputRef}
                    type="text"
                    className={`form-control ai-input ${theme === 'dark' ? 'theme__div' : ''}`}
                    placeholder="Type your query..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    disabled={isTyping}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary ai-send-button"
                    disabled={isTyping || input.trim() === ""}
                  >
                    ➤
                  </button>
                </form>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AIAssistant;

